// Filtros por coluna para tabelas antd (pedido do Lucas, 23/10/2026).
// Cada helper devolve as props da coluna; a regra de casamento fica em
// filtrosColunaLogica.ts (testada). Use o 4º argumento do onChange da Table
// (`extra.currentDataSource`) para alimentar o <ResumoDoFiltro>.
import type { ReactNode } from 'react';
import { Button, DatePicker, Input, InputNumber, Space, Typography } from 'antd';
import { FilterFilled, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  casaFaixaData, casaFaixaNumero, casaTexto, estatisticas, valoresDistintos,
} from './filtrosColunaLogica';

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const brlFmt = (v: number | null | undefined) =>
  v == null ? '—' : v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

interface DropdownProps {
  selectedKeys: React.Key[];
  setSelectedKeys: (k: React.Key[]) => void;
  confirm: () => void;
  clearFilters?: () => void;
}

// Caixa do filtro: nunca passa da largura da tela (celular).
const caixa = { padding: 8, width: 280, maxWidth: '80vw' } as const;

function Rodape({ p }: { p: DropdownProps }) {
  return (
    <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Button size="small" onClick={() => { p.clearFilters?.(); p.confirm(); }}>Limpar</Button>
      <Button size="small" type="primary" onClick={() => p.confirm()}>Filtrar</Button>
    </Space>
  );
}

const icone = (ativo: boolean) => <FilterFilled style={{ color: ativo ? '#1677ff' : undefined }} />;

/** Filtro de TEXTO: caixa de busca no cabeçalho (ignora acento e caixa). */
export function filtroTexto<T>(getTexto: (r: T) => unknown, rotulo = 'Buscar') {
  return {
    filterDropdown: (p: DropdownProps) => (
      <div style={caixa} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          autoFocus allowClear aria-label={rotulo} placeholder={rotulo}
          prefix={<SearchOutlined />}
          value={(p.selectedKeys[0] as string) || ''}
          onChange={(e) => p.setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => p.confirm()}
          style={{ marginBottom: 8 }}
        />
        <Rodape p={p} />
      </div>
    ),
    filterIcon: (ativo: boolean) => icone(ativo),
    onFilter: (valor: React.Key | boolean, r: T) => casaTexto(getTexto(r), valor),
  };
}

/** Filtro de OPÇÕES: lista com caixa de busca, valores tirados das linhas carregadas. */
export function filtroOpcoes<T>(dados: readonly T[], getValor: (r: T) => unknown) {
  return {
    filters: valoresDistintos(dados.map(getValor)).map((v) => ({ text: v, value: v })),
    filterSearch: true,
    onFilter: (valor: React.Key | boolean, r: T) => String(getValor(r) ?? '').trim() === valor,
  };
}

/** Variante de opções para valor interno ≠ texto exibido (ex.: status → rótulo). */
export function filtroOpcoesRotuladas<T>(
  dados: readonly T[], getChave: (r: T) => string | null | undefined, rotulo: (chave: string) => string,
) {
  const chaves = valoresDistintos(dados.map(getChave));
  return {
    filters: chaves.map((c) => ({ text: rotulo(c), value: c })).sort((a, b) => a.text.localeCompare(b.text, 'pt-BR')),
    filterSearch: true,
    onFilter: (valor: React.Key | boolean, r: T) => (getChave(r) ?? '') === valor,
  };
}

/** Filtro de NÚMERO: mínimo e máximo (inclusivos; um dos dois pode ficar vazio). */
export function filtroNumero<T>(getValor: (r: T) => unknown, opts?: { prefixo?: string }) {
  const ler = (k?: React.Key): [number | null, number | null] => {
    try { const a = JSON.parse(String(k)); return [a[0], a[1]]; } catch { return [null, null]; }
  };
  return {
    filterDropdown: (p: DropdownProps) => {
      const [min, max] = ler(p.selectedKeys[0]);
      const mudar = (a: number | null, b: number | null) =>
        p.setSelectedKeys(a == null && b == null ? [] : [JSON.stringify([a, b])]);
      return (
        <div style={caixa} onKeyDown={(e) => e.stopPropagation()}>
          <Space orientation="vertical" size={8} style={{ width: '100%', marginBottom: 8 }}>
            <InputNumber
              aria-label="Mínimo" placeholder="Mínimo" prefix={opts?.prefixo} style={{ width: '100%' }}
              decimalSeparator="," value={min} onChange={(v) => mudar(v as number | null, max)}
            />
            <InputNumber
              aria-label="Máximo" placeholder="Máximo" prefix={opts?.prefixo} style={{ width: '100%' }}
              decimalSeparator="," value={max} onChange={(v) => mudar(min, v as number | null)}
            />
          </Space>
          <Rodape p={p} />
        </div>
      );
    },
    filterIcon: (ativo: boolean) => icone(ativo),
    onFilter: (valor: React.Key | boolean, r: T) => {
      const [min, max] = ler(valor as React.Key);
      return casaFaixaNumero(getValor(r), min, max);
    },
  };
}

/** Filtro de DATA: intervalo (inclusivo) com o calendário em pt-BR. */
export function filtroData<T>(getValor: (r: T) => unknown) {
  const ler = (k?: React.Key): [string | null, string | null] => {
    try { const a = JSON.parse(String(k)); return [a[0], a[1]]; } catch { return [null, null]; }
  };
  return {
    filterDropdown: (p: DropdownProps) => (
      <div style={caixa} onKeyDown={(e) => e.stopPropagation()}>
        {/* Guarda as DATAS em AAAA-MM-DD (o texto do calendário vem DD/MM/AAAA e não compara
            com a data da linha — revisão de 23/10) e fica controlado: "Limpar" zera o calendário. */}
        <RangePicker
          format="DD/MM/YYYY" style={{ width: '100%', marginBottom: 8 }}
          placeholder={['De', 'Até']} allowClear
          value={(() => {
            const [de, ate] = ler(p.selectedKeys[0]);
            return de || ate ? [de ? dayjs(de) : null, ate ? dayjs(ate) : null] as any : null;
          })()}
          onChange={(d) => {
            const de = d?.[0] ? d[0].format('YYYY-MM-DD') : null;
            const ate = d?.[1] ? d[1].format('YYYY-MM-DD') : null;
            p.setSelectedKeys(de || ate ? [JSON.stringify([de, ate])] : []);
          }}
        />
        <Rodape p={p} />
      </div>
    ),
    filterIcon: (ativo: boolean) => icone(ativo),
    onFilter: (valor: React.Key | boolean, r: T) => {
      const [de, ate] = ler(valor as React.Key);
      return casaFaixaData(getValor(r), de, ate);
    },
  };
}

interface ResumoProps<T> {
  /** Linhas que passaram no filtro (extra.currentDataSource). */
  linhas: readonly T[];
  getValor: (l: T) => unknown;
  /** Quantas linhas a tela carregou (para dizer "dos N carregados" quando há filtro). */
  totalCarregado?: number;
  /** Texto do que está sendo somado, ex.: "itens", "notas". */
  unidade?: string;
  /** Pedaços extras no fim da linha (litros, preço médio...). */
  extra?: ReactNode;
}

/** Uma linha: "N itens · Soma R$ … · Média R$ … · Menor R$ … · Maior R$ …". */
export function ResumoDoFiltro<T>({ linhas, getValor, totalCarregado, unidade = 'itens', extra }: ResumoProps<T>) {
  const e = estatisticas(linhas, getValor);
  const filtrado = totalCarregado != null && totalCarregado !== e.quantidade;
  const partes = [
    `${e.quantidade} ${e.quantidade === 1 ? unidade.replace(/s$/, '') : unidade}${filtrado ? ` (dos ${totalCarregado} carregados)` : ''}`,
    `Soma ${brlFmt(e.soma)}`,
    `Média ${e.comValor ? brlFmt(e.media) : '—'}`,
    `Menor ${brlFmt(e.minimo)}`,
    `Maior ${brlFmt(e.maximo)}`,
  ];
  return (
    <Text type="secondary" style={{ display: 'block' }} aria-live="polite">
      {partes.join(' · ')}{extra}
    </Text>
  );
}

export type FiltrosAtivos = Record<string, (React.Key | boolean)[] | null | undefined>;

/**
 * Aplica à lista os mesmos filtros que a Table aplicou (guardados do onChange).
 * Serve para o resumo acompanhar o filtro mesmo quando os dados recarregam.
 */
export function filtrarLinhas<T>(
  linhas: readonly T[],
  colunas: readonly { key?: React.Key; onFilter?: (v: any, r: T) => boolean }[],
  filtros: FiltrosAtivos,
): T[] {
  const ativas = colunas.filter((c) => c.onFilter && c.key != null && filtros[String(c.key)]?.length);
  if (!ativas.length) return [...linhas];
  return linhas.filter((r) => ativas.every((c) => filtros[String(c.key)]!.some((v) => c.onFilter!(v, r))));
}
